import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Container,
  CircularProgress,
  Chip,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  TrendingUp,
  TrendingDown,
  People,
  PersonAdd,
  PersonRemove,
  Timeline,
  CalendarToday,
  Assessment,
} from '@mui/icons-material';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';

interface RetentionStats {
  dau: number;
  wau: number;
  mau: number;
  retentionRate: number;
  churnRate: number;
  newUsers: number;
  returningUsers: number;
  stickiness: number; // DAU/MAU ratio
}

interface ChurnData {
  period: string;
  churnedUsers: number;
  retainedUsers: number;
  churnRate: number;
}

interface CohortData {
  cohort: string;
  day0: number;
  day1: number;
  day7: number;
  day14: number;
  day30: number;
}

const RetentionDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<RetentionStats | null>(null);
  const [churnData, setChurnData] = useState<ChurnData[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setCohortData] = useState<CohortData[]>([]);
  const [timeRange, setTimeRange] = useState('30d');
  const [activityTrend, setActivityTrend] = useState<any[]>([]);

  const fetchRetentionData = React.useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');

      // Fetch users for analysis
      const usersResponse = await axios.get('https://api.sobcof.com/api/users?page=1&limit=10000', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const analyticsResponse = await axios.get(`https://api.sobcof.com/api/analytics/user-growth?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (usersResponse.data.success) {
        const users = usersResponse.data.data;

        // Calculate DAU, WAU, MAU
        const now = new Date();
        const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

        const dau = users.filter((u: any) =>
          u.lastLoginAt && new Date(u.lastLoginAt) > oneDayAgo
        ).length;

        const wau = users.filter((u: any) =>
          u.lastLoginAt && new Date(u.lastLoginAt) > oneWeekAgo
        ).length;

        const mau = users.filter((u: any) =>
          u.lastLoginAt && new Date(u.lastLoginAt) > oneMonthAgo
        ).length;

        const newUsers = users.filter((u: any) =>
          new Date(u.createdAt) > oneMonthAgo
        ).length;

        const returningUsers = users.filter((u: any) =>
          u.lastLoginAt &&
          new Date(u.createdAt) < oneMonthAgo &&
          new Date(u.lastLoginAt) > oneMonthAgo
        ).length;

        const stickiness = mau > 0 ? (dau / mau) * 100 : 0;

        // Calculate churn (users who haven't logged in for 30 days)
        const activeUsers = users.filter((u: any) =>
          u.lastLoginAt && new Date(u.lastLoginAt) > oneMonthAgo
        ).length;
        const totalUsers = users.length;
        const churnedUsers = totalUsers - activeUsers;
        const churnRate = totalUsers > 0 ? (churnedUsers / totalUsers) * 100 : 0;
        const retentionRate = 100 - churnRate;

        setStats({
          dau,
          wau,
          mau,
          retentionRate,
          churnRate,
          newUsers,
          returningUsers,
          stickiness
        });

        // Generate churn trend data
        const churnTrend: ChurnData[] = [];
        for (let i = 0; i < 12; i++) {
          const periodEnd = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000);
          const periodStart = new Date(periodEnd.getTime() - 7 * 24 * 60 * 60 * 1000);

          const periodActive = users.filter((u: any) =>
            u.lastLoginAt &&
            new Date(u.lastLoginAt) > periodStart &&
            new Date(u.lastLoginAt) <= periodEnd
          ).length;

          const periodTotal = users.filter((u: any) =>
            new Date(u.createdAt) <= periodEnd
          ).length;

          const periodChurned = periodTotal - periodActive;
          const periodChurnRate = periodTotal > 0 ? (periodChurned / periodTotal) * 100 : 0;

          churnTrend.unshift({
            period: `${periodStart.getDate()}/${periodStart.getMonth() + 1}`,
            churnedUsers: periodChurned,
            retainedUsers: periodActive,
            churnRate: periodChurnRate
          });
        }
        setChurnData(churnTrend);

        // Generate activity trend from analytics
        if (analyticsResponse.data.success) {
          setActivityTrend(analyticsResponse.data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching retention data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRetentionData();
  }, [fetchRetentionData]);

  const COLORS = ['#8B4513', '#D2691E', '#CD853F', '#DEB887', '#F4A460'];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#8B4513' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <AppBar
        position="static"
        sx={{
          background: 'linear-gradient(135deg, #8B4513 0%, #D2691E 100%)',
          boxShadow: '0 4px 20px rgba(139, 69, 19, 0.3)'
        }}
      >
        <Toolbar sx={{ minHeight: 70 }}>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')} size="large">
            <ArrowBack />
          </IconButton>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2, width: 40, height: 40 }}>
            <Timeline sx={{ fontSize: 20 }} />
          </Avatar>
          <Typography variant="h5" component="div" sx={{ flexGrow: 1, fontWeight: 600, color: 'white' }}>
            Kullanıcı Elde Tutma Analizi
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 1 }}>
            <Select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              sx={{ color: 'white', '.MuiOutlinedInput-notchedOutline': { border: 0 } }}
            >
              <MenuItem value="7d">Son 7 Gün</MenuItem>
              <MenuItem value="30d">Son 30 Gün</MenuItem>
              <MenuItem value="90d">Son 90 Gün</MenuItem>
            </Select>
          </FormControl>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Key Metrics */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#8B4513', mr: 2 }}>
                    <People />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#8B4513' }}>
                      {stats?.dau || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Günlük Aktif (DAU)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#D2691E', mr: 2 }}>
                    <CalendarToday />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#D2691E' }}>
                      {stats?.wau || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Haftalık Aktif (WAU)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: '#CD853F', mr: 2 }}>
                    <Assessment />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: '#CD853F' }}>
                      {stats?.mau || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Aylık Aktif (MAU)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ bgcolor: stats && stats.stickiness > 20 ? '#4CAF50' : '#FF6B6B', mr: 2 }}>
                    <TrendingUp />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: stats && stats.stickiness > 20 ? '#4CAF50' : '#FF6B6B' }}>
                      {stats?.stickiness.toFixed(1) || 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Stickiness (DAU/MAU)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Retention & Churn */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: '1 1 400px', minWidth: '300px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Kullanıcı Elde Tutma
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress
                      variant="determinate"
                      value={stats?.retentionRate || 0}
                      size={120}
                      thickness={4}
                      sx={{ color: '#4CAF50' }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="h5" component="div" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                        {stats?.retentionRate.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Retention Rate
                  </Typography>
                  <Chip
                    icon={<TrendingUp />}
                    label="Aktif Kullanıcılar"
                    size="small"
                    color="success"
                    sx={{ mt: 1 }}
                  />
                </Box>

                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                    <CircularProgress
                      variant="determinate"
                      value={stats?.churnRate || 0}
                      size={120}
                      thickness={4}
                      sx={{ color: '#FF6B6B' }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: 'absolute',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="h5" component="div" sx={{ fontWeight: 700, color: '#FF6B6B' }}>
                        {stats?.churnRate.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Churn Rate
                  </Typography>
                  <Chip
                    icon={<TrendingDown />}
                    label="Pasif Kullanıcılar"
                    size="small"
                    color="error"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>
            </Card>
          </Box>

          <Box sx={{ flex: '1 1 400px', minWidth: '300px' }}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                Kullanıcı Segmentleri
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Yeni Kullanıcılar', value: stats?.newUsers || 0 },
                      { name: 'Geri Dönen', value: stats?.returningUsers || 0 },
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent ? (percent * 100).toFixed(0) : 0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {COLORS.map((color, index) => (
                      <Cell key={`cell-${index}`} fill={color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 2 }}>
                <Chip
                  icon={<PersonAdd />}
                  label={`${stats?.newUsers || 0} Yeni`}
                  color="primary"
                />
                <Chip
                  icon={<PersonRemove />}
                  label={`${stats?.returningUsers || 0} Geri Dönen`}
                  color="secondary"
                />
              </Box>
            </Card>
          </Box>
        </Box>

        {/* Churn Trend */}
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3, mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Churn Trendi (Son 12 Hafta)
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={churnData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="period" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="churnedUsers"
                stroke="#FF6B6B"
                strokeWidth={2}
                name="Kayıp Kullanıcılar"
              />
              <Line
                type="monotone"
                dataKey="retainedUsers"
                stroke="#4CAF50"
                strokeWidth={2}
                name="Aktif Kullanıcılar"
              />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        {/* User Growth Trend */}
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Kullanıcı Aktivite Trendi
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={activityTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" stroke="#64748b" />
              <YAxis stroke="#64748b" />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="count" fill="#8B4513" name="Yeni Kayıtlar" />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </Container>
    </Box>
  );
};

export default RetentionDashboardPage;
